import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Home, Trash2, Plus, Loader2, Users, Package } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => {
  const t = localStorage.getItem("pr_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

/**
 * Admin — Buyer ↔ Material Type assignments.
 *
 * Curates who owns proposals for which material type. The MRP inbox
 * uses this table for its "Assigned to me" filter and the per-row
 * ownership badge. Any Admin/Finance role can edit.
 */
export default function AdminBuyerAssignments() {
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [assignments, setAssignments] = useState([]);
  const [materialTypes, setMaterialTypes] = useState([]);
  const [counts, setCounts] = useState({});
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");

  // New-assignment inline form.
  const [newForm, setNewForm] = useState({
    mat_code: "", buyer_emp_no: "", buyer_name: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/buyer-assignments`,
        { headers: authHeaders() });
      setAssignments(r.data.assignments || []);
      setMaterialTypes(r.data.material_types || []);
      setCounts(r.data.assignment_counts_per_type || {});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load assignments");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!newForm.mat_code || !newForm.buyer_emp_no) {
      return toast.error("Pick a material type and enter buyer Emp#");
    }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/buyer-assignments`, newForm,
        { headers: authHeaders() });
      toast.success(`Assigned to ${newForm.buyer_name || newForm.buyer_emp_no}`);
      setNewForm({ mat_code: "", buyer_emp_no: "", buyer_name: "" });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(
      `Remove assignment: ${row.buyer_name || row.buyer_emp_no} for ${row.mat_type_name || `code ${row.mat_code}`}?`
    )) return;
    try {
      await axios.delete(`${API}/admin/buyer-assignments`, {
        headers: authHeaders(),
        params: { mat_code: row.mat_code, buyer_emp_no: row.buyer_emp_no },
      });
      toast.success("Removed");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  // Sort + filter the assignments list. Buyers cluster naturally by
  // person so we group by emp_no in the view.
  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return assignments;
    return assignments.filter(a =>
      (a.buyer_name || "").toLowerCase().includes(t) ||
      (a.buyer_emp_no || "").toLowerCase().includes(t) ||
      (a.mat_type_name || "").toLowerCase().includes(t) ||
      String(a.mat_code || "").includes(t)
    );
  }, [assignments, q]);

  const groupedByBuyer = useMemo(() => {
    const g = {};
    for (const a of filtered) {
      const k = `${a.buyer_emp_no}::${a.buyer_name || ""}`;
      if (!g[k]) g[k] = { emp_no: a.buyer_emp_no, name: a.buyer_name, rows: [] };
      g[k].rows.push(a);
    }
    return Object.values(g).sort((x, y) => (x.name || "").localeCompare(y.name || ""));
  }, [filtered]);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" onClick={() => nav("/sap")}
                    data-testid="ba-back-sap">
              <ArrowLeft size={16} className="mr-1" /> SAP Workspace
            </Button>
            <Button variant="ghost" size="sm" onClick={() => nav("/home")}
                    data-testid="ba-back-home">
              <Home size={16} className="mr-1" /> Home
            </Button>
            <div>
              <h1 className="text-base sm:text-lg font-bold text-slate-900 flex items-center gap-2 ml-2">
                <Users size={18} className="text-indigo-700" />
                Buyer Assignments
              </h1>
              <div className="text-xs text-slate-500 ml-2">
                Map material types to buyers · used by MRP inbox "Assigned to me" filter
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-5 space-y-5">
        {/* Add new assignment card */}
        <div className="bg-white border border-slate-200 rounded-sm p-4">
          <div className="text-sm font-bold text-slate-800 mb-2 flex items-center gap-1">
            <Plus size={14} /> New assignment
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
            <label className="text-xs text-slate-600">Material Type
              <select
                value={newForm.mat_code}
                onChange={e => setNewForm({...newForm, mat_code: e.target.value})}
                className="w-full h-8 text-xs border border-slate-300 rounded px-2 mt-0.5"
                data-testid="ba-new-mat-code"
              >
                <option value="">Select…</option>
                {materialTypes.map(m => (
                  <option key={m.code} value={m.code}>
                    {m.code} — {m.name} ({counts[String(m.code)] || 0} assigned)
                  </option>
                ))}
              </select>
            </label>
            <label className="text-xs text-slate-600">Buyer Emp#
              <Input value={newForm.buyer_emp_no}
                     onChange={e => setNewForm({...newForm, buyer_emp_no: e.target.value.trim()})}
                     className="h-8 text-xs mt-0.5"
                     placeholder="e.g. 040001"
                     data-testid="ba-new-emp-no" />
            </label>
            <label className="text-xs text-slate-600">Buyer Name (optional)
              <Input value={newForm.buyer_name}
                     onChange={e => setNewForm({...newForm, buyer_name: e.target.value})}
                     className="h-8 text-xs mt-0.5"
                     placeholder="e.g. OP"
                     data-testid="ba-new-name" />
            </label>
            <div className="flex items-end">
              <Button
                onClick={save} disabled={saving || !newForm.mat_code || !newForm.buyer_emp_no}
                className="w-full h-8 bg-indigo-700 hover:bg-indigo-800"
                data-testid="ba-save-btn"
              >
                {saving ? <Loader2 size={13} className="animate-spin mr-1"/> : <Plus size={13} className="mr-1"/>}
                Assign
              </Button>
            </div>
          </div>
        </div>

        {/* Search bar */}
        <div className="flex items-center gap-2">
          <Input placeholder="Filter by buyer, emp#, or material type…"
                 value={q} onChange={e => setQ(e.target.value)}
                 className="max-w-sm h-8 text-sm"
                 data-testid="ba-search" />
          <div className="text-xs text-slate-500">
            {assignments.length} total · {groupedByBuyer.length} buyers · {materialTypes.length} material types available
          </div>
        </div>

        {/* Grouped list */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="animate-spin text-indigo-600" size={26} />
          </div>
        ) : groupedByBuyer.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-sm p-8 text-center">
            <Package size={40} className="mx-auto text-slate-300 mb-3" />
            <div className="text-sm text-slate-600">
              {assignments.length === 0
                ? "No assignments yet. Create one above so buyers see 'Assigned to me' in the MRP inbox."
                : "No matches."}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {groupedByBuyer.map((g) => (
              <div key={g.emp_no} className="bg-white border border-slate-200 rounded-sm overflow-hidden"
                   data-testid={`ba-buyer-${g.emp_no}`}>
                <div className="bg-slate-100 px-3 py-2 flex items-center justify-between">
                  <div>
                    <span className="font-mono text-xs font-bold text-slate-800">{g.emp_no}</span>
                    <span className="ml-2 text-sm font-semibold text-slate-900">{g.name || "—"}</span>
                  </div>
                  <div className="text-xs text-slate-600">
                    {g.rows.length} material type{g.rows.length === 1 ? "" : "s"}
                  </div>
                </div>
                <table className="w-full text-sm">
                  <tbody className="divide-y divide-slate-50">
                    {g.rows.map((r) => (
                      <tr key={`${r.mat_code}::${r.buyer_emp_no}`}
                          className="hover:bg-slate-50">
                        <td className="px-3 py-1.5 w-24 font-mono text-xs text-slate-500">
                          {r.mat_code}
                        </td>
                        <td className="px-3 py-1.5 text-sm text-slate-800">
                          {r.mat_type_name || "—"}
                        </td>
                        <td className="px-3 py-1.5 text-right">
                          <Button variant="ghost" size="sm"
                                  onClick={() => remove(r)}
                                  className="h-7 w-7 p-0"
                                  data-testid={`ba-delete-${r.mat_code}-${r.buyer_emp_no}`}>
                            <Trash2 size={13} className="text-red-600" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
